import { z } from 'zod';
import { MoneyNum } from './numbers';

/* ─────────── Управление изменениями (раздел 6.11) ─────────── */

const DateStr = z.iso.date();

/** Что меняется (таблица раздела 6.11) */
export const CHANGE_TYPES = {
  psd: 'ПСД',
  estimate: 'Смета',
  material: 'ТМЦ, замена на аналог',
  contractor: 'Подрядчик',
  schedule: 'График',
  contract: 'Договор',
  norms: 'Справочники и нормы',
} as const;
export type ChangeType = keyof typeof CHANGE_TYPES;

/** Класс запроса (ИЗ-16) */
export const CHANGE_CLASSES = {
  within_contract: 'В пределах договора',
  customer: 'За счёт заказчика',
  reserve: 'За счёт резерва',
  reallocation: 'Только перераспределение бюджета',
} as const;
export type ChangeClass = keyof typeof CHANGE_CLASSES;

/** Инициатор и за чей счёт (ИЗ-01, ИЗ-06) */
export const CHANGE_PARTIES = {
  customer: 'Заказчик',
  designer: 'Проектировщик',
  contractor: 'Подрядчик',
  us: 'Мы',
} as const;
export type ChangeParty = keyof typeof CHANGE_PARTIES;

export const CHANGE_STATUSES = {
  draft: 'Черновик',
  approval: 'На согласовании',
  approved: 'Утверждён',
  rejected: 'Отклонён',
  cancelled: 'Отменён',
  implemented: 'Исполнен',
} as const;
export type ChangeStatus = keyof typeof CHANGE_STATUSES;

/** Шаги маршрута (ИЗ-03, Регламент п. 3.8) */
export const CHANGE_STEPS = {
  pto: 'ПТО',
  estimator: 'Сметчик',
  pm: 'Руководитель проекта',
  finance: 'Финансовый менеджер',
  curator: 'Куратор проекта',
  director: 'Директор',
  designer: 'Проектировщик',
  customer: 'Заказчик',
} as const;
export type ChangeStep = keyof typeof CHANGE_STEPS;

/**
 * Право, которым решается шаг. Директор — право согласовывать изменения на юрлицо или компанию,
 * куратор — утверждать бюджет; проектировщик и заказчик решают сами в портале или их решение по письму
 * вносит руководитель проекта.
 */
export const CHANGE_STEP_PERMS: Record<ChangeStep, string> = {
  pto: 'psd.approve',
  estimator: 'estimate.edit',
  pm: 'changes.approve',
  finance: 'finance.edit',
  curator: 'budget.approve',
  director: 'changes.approve',
  designer: 'psd.upload',
  customer: 'changes.view',
};

/** Что затрагивает запрос (ИЗ-01): листы, комплекты, договоры, заявки, вехи, разделы бюджета */
export const CHANGE_LINKS = {
  set: 'Комплект ПСД',
  sheet: 'Лист ПСД',
  contract: 'Договор',
  request: 'Заявка на закуп',
  milestone: 'Веха',
  activity: 'Работа графика',
  budget_line: 'Раздел бюджета',
  estimate: 'Смета',
  other: 'Другое',
} as const;
export type ChangeLink = keyof typeof CHANGE_LINKS;

export const URGENCY_CH = { normal: 'Обычная', urgent: 'Срочная' } as const;

export const ChangeItemInput = z.object({
  linkType: z.enum(Object.keys(CHANGE_LINKS) as [ChangeLink, ...ChangeLink[]]),
  linkId: z.uuid().nullable().optional(),
  description: z.string().trim().max(1000).nullable().optional(),
  amountDelta: MoneyNum.nullable().optional(),
});

export const ChangeInput = z.object({
  projectId: z.uuid(),
  title: z.string().trim().min(3).max(300),
  type: z.enum(Object.keys(CHANGE_TYPES) as [ChangeType, ...ChangeType[]]),
  changeClass: z.enum(Object.keys(CHANGE_CLASSES) as [ChangeClass, ...ChangeClass[]]),
  initiator: z.enum(Object.keys(CHANGE_PARTIES) as [ChangeParty, ...ChangeParty[]]),
  payer: z.enum(Object.keys(CHANGE_PARTIES) as [ChangeParty, ...ChangeParty[]]),
  reason: z.string().trim().min(3).max(5000),
  /** Документ-основание: письмо, ответ на вопрос, замечание экспертизы */
  basis: z.string().trim().max(500).nullable().optional(),
  urgency: z.enum(['normal', 'urgent']).optional(),
  /** Изменение стоимости для заказчика (договорной цены), ₸ */
  revenueDelta: MoneyNum.nullable().optional(),
  /** Изменение себестоимости, ₸ */
  costDelta: MoneyNum.nullable().optional(),
  /** Стоимость затронутой позиции — для лимита мелких изменений (ИЗ-05) */
  baseAmount: MoneyNum.nonnegative().nullable().optional(),
  /** Сдвиг срока, календарных дней */
  daysDelta: z.number().int().min(-3650).max(3650).nullable().optional(),
  /** Меняются ФП или ДДС: в маршрут — финансовый менеджер и куратор (ИЗ-15, Регламент п. 3.8) */
  affectsFinPlan: z.boolean().optional(),
  /** Нужно согласование проектировщика */
  needsDesigner: z.boolean().optional(),
  /** Работы уже идут до подписания допсоглашения — риск (ИЗ-07) */
  workStarted: z.boolean().optional(),
  questionId: z.uuid().nullable().optional(),
  remarkId: z.uuid().nullable().optional(),
  items: z.array(ChangeItemInput).max(100).optional(),
});
export type ChangeInput = z.infer<typeof ChangeInput>;

export const ChangePatch = ChangeInput.omit({ projectId: true, questionId: true, remarkId: true }).partial();

export const CHANGE_ACTIONS = {
  submit: 'Отправить на согласование',
  approve: 'Согласовать',
  reject: 'Отклонить',
  revise: 'Вернуть в черновик',
  cancel: 'Отменить',
  implement: 'Отметить исполненным',
  work_started: 'Работы начаты',
  amendment_signed: 'Допсоглашение подписано',
  comment: 'Комментарий',
} as const;
export type ChangeAction = keyof typeof CHANGE_ACTIONS;

export const ChangeActionInput = z.object({
  action: z.enum(Object.keys(CHANGE_ACTIONS) as [ChangeAction, ...ChangeAction[]]),
  step: z.enum(Object.keys(CHANGE_STEPS) as [ChangeStep, ...ChangeStep[]]).optional(),
  comment: z.string().trim().max(5000).nullable().optional(),
  date: DateStr.optional(),
});

export const ChangeSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  number: z.string(),
  title: z.string(),
  type: z.enum(Object.keys(CHANGE_TYPES) as [ChangeType, ...ChangeType[]]),
  changeClass: z.enum(Object.keys(CHANGE_CLASSES) as [ChangeClass, ...ChangeClass[]]),
  initiator: z.enum(Object.keys(CHANGE_PARTIES) as [ChangeParty, ...ChangeParty[]]),
  payer: z.enum(Object.keys(CHANGE_PARTIES) as [ChangeParty, ...ChangeParty[]]),
  status: z.enum(Object.keys(CHANGE_STATUSES) as [ChangeStatus, ...ChangeStatus[]]),
  minor: z.boolean(),
  /** Суммы скрыты без уровня «Все деньги» (ДС-04) */
  revenueDelta: z.number().nullable(),
  costDelta: z.number().nullable(),
  daysDelta: z.number().nullable(),
  /** За счёт заказчика, работы идут, допсоглашение не подписано — риск в марже и деньгах (ИЗ-07) */
  atRisk: z.boolean(),
  amendmentSignedAt: z.string().nullable(),
  basis: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  /** Шаги, которые ждут решения */
  waiting: z.array(z.string()),
});
export type ChangeSummary = z.infer<typeof ChangeSummary>;

export const ChangeDetail = ChangeSummary.extend({
  reason: z.string(),
  urgency: z.enum(['normal', 'urgent']),
  baseAmount: z.number().nullable(),
  affectsFinPlan: z.boolean(),
  needsDesigner: z.boolean(),
  workStarted: z.boolean(),
  round: z.number(),
  moneyHidden: z.boolean(),
  question: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  remark: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  /** Акт допработ с площадки (ПР-20), по подписи которого создан запрос */
  extraWorkAct: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  /** Анализ влияния по связям (ИЗ-02): что затронуто и в каком оно состоянии */
  items: z.array(
    z.object({
      id: z.uuid(),
      linkType: z.enum(Object.keys(CHANGE_LINKS) as [ChangeLink, ...ChangeLink[]]),
      linkId: z.uuid().nullable(),
      title: z.string(),
      path: z.string().nullable(),
      facts: z.array(z.string()),
      description: z.string().nullable(),
      amountDelta: z.number().nullable(),
    }),
  ),
  approvals: z.array(
    z.object({
      step: z.enum(Object.keys(CHANGE_STEPS) as [ChangeStep, ...ChangeStep[]]),
      status: z.enum(['pending', 'approved', 'rejected']),
      decidedBy: z.string().nullable(),
      decidedAt: z.string().nullable(),
      comment: z.string().nullable(),
      /** Этот человек может решить шаг */
      mine: z.boolean(),
    }),
  ),
  events: z.array(
    z.object({
      at: z.string(),
      by: z.string().nullable(),
      action: z.string(),
      comment: z.string().nullable(),
    }),
  ),
  actions: z.array(z.enum(Object.keys(CHANGE_ACTIONS) as [ChangeAction, ...ChangeAction[]])),
});
export type ChangeDetail = z.infer<typeof ChangeDetail>;

/** Итог по проекту (ИЗ-06, ИЗ-16): утверждённые и неутверждённые отдельно */
export const ChangeTotals = z.object({
  count: z.number(),
  approved: z.object({
    count: z.number(),
    revenue: z.number().nullable(),
    cost: z.number().nullable(),
    days: z.number(),
  }),
  pending: z.object({
    count: z.number(),
    revenue: z.number().nullable(),
    cost: z.number().nullable(),
    days: z.number(),
  }),
  atRisk: z.object({ count: z.number(), revenue: z.number().nullable() }),
  /** Тренд (ИЗ-12): накопленные утверждённые изменения по датам */
  trend: z.array(
    z.object({
      date: z.string(),
      revenue: z.number().nullable(),
      cost: z.number().nullable(),
      days: z.number(),
    }),
  ),
  byType: z.array(
    z.object({ key: z.string(), count: z.number(), revenue: z.number().nullable(), days: z.number() }),
  ),
  byInitiator: z.array(
    z.object({ key: z.string(), count: z.number(), revenue: z.number().nullable(), days: z.number() }),
  ),
  moneyHidden: z.boolean(),
});
export type ChangeTotals = z.infer<typeof ChangeTotals>;

export const ChangeSettings = z.object({
  /** Мелкое изменение: до этого процента стоимости позиции — без маршрута (ИЗ-05) */
  minorPercent: z.number().min(0).max(20),
  /** Выше этой суммы в маршрут добавляется Директор (ИЗ-03), ₸ */
  directorLimit: MoneyNum.nonnegative(),
});
export type ChangeSettings = z.infer<typeof ChangeSettings>;

export const DEFAULT_CHANGE_SETTINGS: ChangeSettings = { minorPercent: 2, directorLimit: 10_000_000 };

export function changeNumber(n: number): string {
  return `ИЗ-${String(n).padStart(4, '0')}`;
}
